import { type QueryClient, skipToken, useQuery } from '@tanstack/react-query'

import { FEATURE_FLAGS } from '@/lib/workspaces/feature-flag-names'

/**
 * Per-workspace Inbox performance rollouts. The workspace bootstrap reads them
 * on every document load and hydrates them; the client never fetches them.
 */
export const INBOX_PERF_FLAGS = [FEATURE_FLAGS.inboxClientSwitch] as const

export type InboxPerfFlagName = (typeof INBOX_PERF_FLAGS)[number]
export type InboxPerfFlags = Readonly<Record<InboxPerfFlagName, boolean>>

const INBOX_PERF_FLAGS_QUERY_ROOT = 'inbox-perf-flags'

export const inboxPerfFlagsQueryKey = (workspaceId: string | undefined) =>
  [INBOX_PERF_FLAGS_QUERY_ROOT, workspaceId] as const

/**
 * Hydration replaces cached data only with strictly newer data. Every server
 * render stamps this same time, so the first value a bootstrap delivers is the
 * one the page session keeps: a later render (router.refresh, a server action)
 * cannot switch an Inbox flow mid-session. Turning a flag off takes effect on
 * reload. Reading never stores a value, so a read made before that (a hidden
 * previous page rendering the next workspace's id before its bootstrap streams
 * in) cannot block it.
 */
const INBOX_PERF_FLAGS_UPDATED_AT = 1

/**
 * Server side of the contract: the value the workspace bootstrap hydrates,
 * from a read that may include other flags. A failed bootstrap hydrates
 * nothing, so the flags read off until a later bootstrap delivers them.
 */
export function primeInboxPerfFlags(
  queryClient: QueryClient,
  workspaceId: string,
  flags: InboxPerfFlags
) {
  const value = Object.fromEntries(
    INBOX_PERF_FLAGS.map((name) => [name, flags[name]])
  ) as InboxPerfFlags
  queryClient.setQueryData(inboxPerfFlagsQueryKey(workspaceId), value, {
    updatedAt: INBOX_PERF_FLAGS_UPDATED_AT,
  })
}

/**
 * Keep the hydrated value for the whole page session, including the time
 * before any Inbox screen observes it, and never refetch it.
 */
export function retainInboxPerfFlags(queryClient: QueryClient) {
  queryClient.setQueryDefaults([INBOX_PERF_FLAGS_QUERY_ROOT], {
    queryFn: skipToken,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * Whether an Inbox performance rollout is on for this workspace. Reads only
 * the bootstrap value and is off until one arrives; impersonation and a failed
 * bootstrap deliver none. Read it where the behavior is decided: it can change
 * once per workspace, from off to the delivered value.
 */
export function useInboxPerfFlag(workspaceId: string | undefined, name: InboxPerfFlagName) {
  const { data } = useQuery<InboxPerfFlags>({
    queryKey: inboxPerfFlagsQueryKey(workspaceId),
    queryFn: skipToken,
    enabled: false,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
  })
  return data?.[name] === true
}
